import { useStaticQuery, graphql, Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import Button from "../../components/Buttons/Button";
import { ButtonProps } from "../../../custom";
import { Flex } from "theme-ui";
import TrustPilotReviews from "../../components/Trustpilot/TrustpilotReviews";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import MobileAppButtons from "../../components/Buttons/MobileAppButtons";

interface Props {}
const Arrow = ({
  direction,
  moveSlide,
  arrowColor = "black",
}: {
  direction: "left" | "right";
  moveSlide: (value: string) => void;
  arrowColor?: string;
}) => {
  return direction === "left" ? (
    <svg
      width="40"
      height="30"
      viewBox="0 0 50 232"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      onClick={(e: Event) => moveSlide(e)}
      sx={{
        position: "relative",
        zIndex: 9,
        left: [0, null, null, -30, -40],
        cursor: "pointer",
      }}
    >
      <g>
        <path
          d="M4 112L107.5 0.282715L107.5 223.717L4 112Z"
          fill={arrowColor}
        />
      </g>
    </svg>
  ) : (
    <svg
      width="40"
      height="30"
      viewBox="0 0 50 232"
      fill="none"
      sx={{
        position: "relative",
        zIndex: 9,
        cursor: "pointer",
        right: ["5px", null, null, -10, -20],
      }}
      xmlns="http://www.w3.org/2000/svg"
      onClick={(e: Event) => moveSlide(e)}
    >
      <g>
        <path
          d="M108 112L4.49988 223.717L4.49988 0.282724L108 112Z"
          fill={arrowColor}
        />
      </g>
    </svg>
  );
};
const PromoCarouselBlock = (props: Props) => {
  const data = useStaticQuery(graphql`
    query PromoCarouselBlockQuery {
      allDataJson {
        edges {
          node {
            blocks {
              PromoCarouselBlock {
                backgroundGradient
                slides {
                  image
                  heading
                  subText
                  downloadApp
                  to
                  href
                  component
                  button1 {
                    to
                    href
                    text
                    variant
                  }
                  button2 {
                    to
                    text
                    variant
                  }
                }
              }
            }
          }
        }
      }
      myaccount: file(relativePath: { eq: "slides/MyAccountGraphic.png" }) {
        childImageSharp {
          gatsbyImageData(width: 400, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      refer: file(relativePath: { eq: "slides/ReferAFriendGraphic.png" }) {
        childImageSharp {
          gatsbyImageData(width: 300, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      refi: file(relativePath: { eq: "slides/RefiGraphic.png" }) {
        childImageSharp {
          gatsbyImageData(width: 300, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      erefi: file(relativePath: { eq: "slides/erefi.png" }) {
        childImageSharp {
          gatsbyImageData(width: 400, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      lending: file(relativePath: { eq: "slides/LendingAHandGraphic.png" }) {
        childImageSharp {
          gatsbyImageData(width: 150, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      apple: file(
        relativePath: { eq: "download-app/download-on-the-app-store.png" }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      google: file(
        relativePath: { eq: "download-app/google-play-badge-logo.png" }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
    }
  `);

  const {
    PromoCarouselBlock: { backgroundGradient, slides },
  } = data.allDataJson.edges[0].node.blocks;
  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: true,
    },
    [
      (slider) => {
        let timeout: ReturnType<typeof setTimeout>;
        let mouseOver = false;
        function clearNextTimeout() {
          clearTimeout(timeout);
        }
        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver) return;
          timeout = setTimeout(() => {
            slider.next();
          }, 3000);
        }
        slider.on("created", () => {
          slider.container.addEventListener("mouseover", () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener("mouseout", () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });
        slider.on("dragStarted", clearNextTimeout);
        slider.on("animationEnded", nextTimeout);
        slider.on("updated", nextTimeout);
      },
    ]
  );
  return (
    <div
      sx={{
        background: backgroundGradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: [730, null, null, 600],
        width: "100%",
        px: [3, null, null, 5],
      }}
    >
      <Arrow
        arrowColor={"#2AA2DA"}
        direction="left"
        moveSlide={(e: any) =>
          e.stopPropagation() || instanceRef.current?.prev()
        }
      />
      <div ref={sliderRef} className="keen-slider">
        {slides.map(
          (
            slide: {
              button1: ButtonProps;
              button2: ButtonProps;
              heading: string;
              image: string;
              component: boolean;
              subText: string;
              to: string;
              href: string;
              downloadApp: boolean;
            },
            i: number
          ) => (
            <Flex
              key={i}
              className="keen-slider__slide"
              sx={{
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: [0, null, null, 6],
                width: "100%",
              }}
            >
              {slide.to ? (
                <Link
                  to={slide.to}
                  sx={{
                    textDecoration: "none",
                  }}
                >
                  <div
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      px: 2,
                      textDecoration: "none",
                      color: "darkBlue",
                    }}
                  >
                    {slide.component && (
                      <TrustPilotReviews
                        variant="trust-score"
                        level="Excellent"
                      />
                    )}
                    {slide.image && (
                      <GatsbyImage
                        image={
                          data[slide.image].childImageSharp.gatsbyImageData
                        }
                        alt=""
                        sx={{
                          alignSelf: "center",
                        }}
                      />
                    )}
                    <h2
                      sx={{
                        variant: "text.h2",
                        fontSize: [3, null, null, 32],
                        color: "brand.darkBlue",
                        textAlign: "center",
                        fontWeight: 900,
                        px: 2,
                      }}
                      dangerouslySetInnerHTML={{ __html: slide.heading }}
                    />

                    {slide.subText && (
                      <p
                        sx={{
                          fontSize: [16, null, null, 18],
                          lineHeight: 1.5,
                          m: 0,
                          textAlign: "center",

                          px: [3, null, null, 6],
                        }}
                        dangerouslySetInnerHTML={{ __html: slide.subText }}
                      />
                    )}
                  </div>
                </Link>
              ) : (
                <a
                  href={slide.href}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    px: 2,
                    textDecoration: "none",
                    color: "darkBlue",
                  }}
                >
                  {slide.component && (
                    <TrustPilotReviews
                      variant="trust-score"
                      level="Excellent"
                    />
                  )}
                  {slide.image && (
                    <GatsbyImage
                      sx={{
                        objectFit: "contain",
                      }}
                      image={data[slide.image].childImageSharp.gatsbyImageData}
                      alt=""
                    />
                  )}
                  <h2
                    sx={{
                      variant: "text.h2",
                      fontSize: [3, null, null, 32],
                      color: "brand.darkBlue",
                      textAlign: "center",

                      fontWeight: 900,
                      px: 2,
                    }}
                    dangerouslySetInnerHTML={{ __html: slide.heading }}
                  />
                  {slide.downloadApp && <MobileAppButtons size={200} />}
                  {slide.subText && (
                    <p
                      sx={{
                        fontSize: [16, null, null, 18],
                        lineHeight: 1.5,
                        textAlign: "center",

                        m: 0,
                        px: [3, null, null, 6],
                      }}
                      dangerouslySetInnerHTML={{ __html: slide.subText }}
                    />
                  )}
                </a>
              )}
              {slide.button1 && <Button {...slide.button1} />}
              {slide.button2 && <Button {...slide.button2} sx={{ mt: 3 }} />}
            </Flex>
          )
        )}
      </div>
      <Arrow
        arrowColor={"#2AA2DA"}
        direction="right"
        moveSlide={(e: any) =>
          e.stopPropagation() || instanceRef.current?.next()
        }
      />
    </div>
  );
};

export default PromoCarouselBlock;
